import { useCallback, useRef, useEffect } from 'react';
import { ConversationState } from '../types/conversation';

/**
 * Enhanced WebSocket hook with improved error handling and reconnection logic
 */
export const useWebSocket = (
  url: string,
  onMessage: (message: any) => void,
  onConnectionStatusChange: (status: ConversationState['connectionStatus']) => void,
  onError: (error: string) => void
) => {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000;
  const pendingMessagesRef = useRef<any[]>([]);
  const isConnectingRef = useRef(false);

  /**
   * Cleans up resources and resets connection state
   */
  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (wsRef.current) {
      try {
        if (wsRef.current.readyState === WebSocket.OPEN || 
            wsRef.current.readyState === WebSocket.CONNECTING) {
          wsRef.current.close(1000, "Cleanup");
        }
      } catch (e) {
        console.error("Error during WebSocket cleanup:", e);
      }
      wsRef.current = null;
    }
    
    isConnectingRef.current = false;
  }, []);

  /**
   * Creates and manages a WebSocket connection
   */  const connect = useCallback(() => {
    // Prevent multiple connection attempts
    if (isConnectingRef.current) {
      console.log("Connection attempt already in progress");
      return;
    }
    
    // Clean up any existing connection
    cleanup();
    isConnectingRef.current = true;
    
    try {
      console.log("Creating new WebSocket connection to:", url);
      onConnectionStatusChange('connecting');
      
      wsRef.current = new WebSocket(url);
      
      wsRef.current.onopen = () => {
        console.log("WebSocket connection established");
        onConnectionStatusChange('connected');
        reconnectAttempts.current = 0;
        isConnectingRef.current = false;
        
        // Send any pending messages
        if (pendingMessagesRef.current.length > 0) {
          console.log(`Sending ${pendingMessagesRef.current.length} pending messages`);
          const messages = [...pendingMessagesRef.current];
          pendingMessagesRef.current = [];
          
          messages.forEach(msg => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              try {
                wsRef.current.send(JSON.stringify(msg));
              } catch (e) {
                console.error("Error sending pending message:", e);
                pendingMessagesRef.current.push(msg);
              }
            } else {
              pendingMessagesRef.current.push(msg);
            }
          });
        }
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          console.log("Received message of type:", message.type);
          onMessage(message);
        } catch (e) {
          console.error("Error parsing message:", e);
          onError("Failed to process server response");
        }
      };
      
      wsRef.current.onclose = (event) => {
        console.log(`WebSocket closed with code ${event.code} and reason: ${event.reason}`);
        onConnectionStatusChange('disconnected');
        isConnectingRef.current = false;
        
        // Only attempt reconnect for abnormal closures
        if (event.code !== 1000 && event.code !== 1001) {
          if (reconnectAttempts.current < maxReconnectAttempts) {
            reconnectAttempts.current++;
            console.log(`Scheduling reconnect attempt ${reconnectAttempts.current}/${maxReconnectAttempts}`);
            
            reconnectTimeoutRef.current = setTimeout(() => {
              console.log("Attempting reconnection...");
              connect();
            }, reconnectDelay);
          } else {
            console.error("Max reconnection attempts reached");
            onError("Unable to maintain connection to server");
          }
        }
      };
      
      wsRef.current.onerror = (event) => {
        console.error("WebSocket error:", event);
        // Error events are followed by close events, so we handle reconnection there
      };
      
    } catch (e) {
      console.error("Failed to establish WebSocket connection:", e);
      onError("Failed to connect to server");
      onConnectionStatusChange('disconnected');
      isConnectingRef.current = false;
    }
  }, [url, onMessage, onConnectionStatusChange, onError, cleanup]);

  /**
   * Manually disconnect the WebSocket
   */
  const disconnect = useCallback(() => {
    console.log("Manual WebSocket disconnect requested");
    cleanup();
    onConnectionStatusChange('disconnected');
    reconnectAttempts.current = 0;
  }, [cleanup, onConnectionStatusChange]);

  /**
   * Send a message through the WebSocket connection
   */
  const sendMessage = useCallback((message: any) => {
    console.log("Attempting to send message:", message.type);
    
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(JSON.stringify(message));
        console.log("Message sent successfully");
      } catch (e) {
        console.error("Error sending message:", e);
        pendingMessagesRef.current.push(message);
        onError("Failed to send message");
      }
    } else {
      console.warn("WebSocket not ready, queueing message");
      pendingMessagesRef.current.push(message);
      
      // Try to establish connection if not already trying
      if (!isConnectingRef.current && (!wsRef.current || wsRef.current.readyState > WebSocket.OPEN)) {
        connect();
      }
    }
  }, [connect, onError]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      console.log("Component unmounting, cleaning up WebSocket");
      cleanup();
    };
  }, [cleanup]);

  return {
    connect,
    disconnect,
    sendMessage,
  };
};
